/**
 * Статический whitelist `/api/r`: трекеры-входы и конечные домены партнёров.
 * Живёт отдельно от route, чтобы клиентский AffiliateLink решал «оборачивать или нет»
 * по тому же списку, что проверяет сервер: прямую ссылку на домен вне списка
 * route отдаёт 400, поэтому оборачивать можно только то, что он гарантированно пропустит.
 * Домены активных провайдеров из Sanity route добавляет динамически — на клиенте их нет,
 * такие ссылки остаются как были.
 */

// Whitelist входных tracker-хостов. Защита от open-redirect: чужой URL через ?u не пропустим.
const TRACKER_HOSTS = new Set(['tp.media', 'travelpayouts.com']);
// `.ojrq.net` — промежуточный хоп Impact (pxf.io → ojrq.net → партнёр); оставлен
// на случай ссылок, сгенерированных сразу на этот хоп.
const TRACKER_SUFFIXES = ['.tp.st', '.tpo.li', '.sjv.io', '.pxf.io', '.ojrq.net'];

export function isTrackerHost(host) {
    const h = (host || '').toLowerCase();
    if (TRACKER_HOSTS.has(h)) return true;
    return TRACKER_SUFFIXES.some(s => h === s.slice(1) || h.endsWith(s));
}

// Exact registrable domains only. Patterns such as `brand.[a-z.]+` are unsafe:
// they also match attacker-owned hosts like `brand.com.evil.test`.
const DESTINATION_DOMAINS = new Set([
    'booking.com', 'trip.com', 'ctrip.com',
    'tripadvisor.com', 'tripadvisor.co.uk',
    'tripster.com', 'tripster.ru',
    'kiwitaxi.com', 'kiwitaxi.ru',
    'welcomepickups.com', 'localrent.com', 'airalo.com',
    // Продуктовый домен AmigoSIM: в ссылках amigoesim.pxf.io он вшит как цель (?u=),
    // а в Sanity есть только трекер — без него 16 ссылок на eSIM-страницах
    // уезжали на главную (найдено сканом 28.08).
    'amigosim.com',
    // Продуктовый домен Ubigi: ссылки-источники в постах ведут на
    // `cellulardata.ubigi.com`, и без него route отдавал 400 — читатель попадал
    // на ошибку вместо магазина (найдено 29.09.2026 в трёх постах про eSIM).
    'ubigi.com',
    // Трекер Saily (go.saily.site) и Черехапа (подключена 29.09) — чтобы AffiliateLink оборачивал
    // их ссылки в /api/r и без доменов из Sanity (краул 29.09.2026: 40 голых ссылок).
    'saily.site', 'cherehapa.ru',
    'aviasales.com', 'aviasales.ru',
    'bikesbooking.com', 'searadar.com', 'economybookings.com', 'discovercars.com',
    'omio.com', 'omio.ru', 'gomio.com',
    'intui.travel', 'intuitravel.com',
    'getyourguide.com', 'getyourguide.ru', 'getyourguide.de',
    'getyourguide.fr', 'getyourguide.it', 'getyourguide.es',
    'getyourguide.co.uk', 'getyourguide.com.br', 'getyourguide.com.mx',
    'getyourguide.com.au', 'getyourguide.ca', 'getyourguide.nl',
    'getyourguide.pl', 'getyourguide.pt', 'getyourguide.jp',
    'viator.com',
]);

const TP_HOST = /(^|\.)(tp\.media|tpo\.li|tp\.st)$/i;
const IMPACT_HOST = /(^|\.)(pxf\.io|sjv\.io|ojrq\.net)$/i;
// Часть программ Impact выдаёт ссылки на собственном домене вместо *.pxf.io, и по
// хвосту домена их не отличить от чужих. Отсюда точный список: Ubigi за 16–18.09
// дал 42 клика и 0% с меткой, пока сеть считала их как трафик без страницы.
// `ref.airalo.com` — короткие реферальные ссылки Airalo: редирект живой, но без
// `subId1` сеть не знает страницу (29.09.2026 — 13 кликов Airalo из 27 без метки).
const IMPACT_VANITY_HOSTS = new Set(['go.ubigi.com', 'ref.airalo.com']);
const isImpactHost = (host) => IMPACT_HOST.test(host) || IMPACT_VANITY_HOSTS.has(host);
// GetYourGuide ведёт учёт кампаний по `cmp`; без него весь трафик партнёра падает в
// «no_reseller_campaign» — 30 ссылок на одной странице туров уходили без метки страницы
// (проверено 18.09.2026). Ссылки идут прямо на продуктовый домен, трекера у сети нет.
const GYG_HOST = /(^|\.)getyourguide\.[a-z.]+$/i;
// Localrent — 76% денег сайта, и до 18.09.2026 ни одна бронь не знала, с какой страницы пришла:
// в кабинете метка называется `marker` (генератор партнёрских ссылок отдаёт
// `?marker=<метка>&r=11074`), а в ссылках сайта её не было. Важно: короткие ссылки
// `localrent.com/l/<code>/` теряют marker на редиректе — метку доносит только полный URL.
const LOCALRENT_HOST = /(^|\.)localrent\.com$/i;

/**
 * Дописывает нашу метку страницы в трекерную ссылку сети, если своей метки там нет:
 * Travelpayouts — `sub_id` (проверено 16.09: короткие tpo.li/tp.st его записывают),
 * Impact — `subId1`, GetYourGuide — `cmp`, Localrent — `marker` (кроме коротких ссылок
 * `/l/<code>/`: они теряют метку на редиректе). До этого subid оседал только в логе /api/r,
 * и 71% редиректов TP за 01.08–15.09 приходили без метки (аудит 2026-09-16, §5).
 * Строкой, а не через URLSearchParams: тот перекодирует вложенный u= у tp.media.
 */
const CHEREHAPA_HOST = /(^|\.)cherehapa\.ru$/i;

export function withNetworkSubId(target, subid) {
    if (!subid || typeof target !== 'string') return target;
    let parsed;
    try {
        parsed = new URL(target);
    } catch {
        return target;
    }
    // Cherehapa: marker=<страна> зашит в офферинги, и клики hero/bottom/sticky и разные страницы
    // в отчёте не различались (обход 01.10.2026) — дописываем метку страницы к стране.
    if (CHEREHAPA_HOST.test(parsed.hostname)) {
        const value = String(subid).replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);
        const prev = parsed.searchParams.get('marker');
        if (!value || (prev && prev.includes(value))) return target;
        parsed.searchParams.set('marker', prev ? `${prev}-${value}` : value);
        return parsed.toString();
    }
    const param = TP_HOST.test(parsed.hostname) ? 'sub_id'
        : isImpactHost(parsed.hostname) ? 'subId1'
            : GYG_HOST.test(parsed.hostname) ? 'cmp'
                : LOCALRENT_HOST.test(parsed.hostname) && !/^\/l\//.test(parsed.pathname) ? 'marker' : null;
    if (!param || parsed.searchParams.has(param)) return target;
    const value = String(subid).replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 100);
    if (!value) return target;
    const [base, hash] = target.split('#');
    const sep = base.includes('?') ? (base.endsWith('?') || base.endsWith('&') ? '' : '&') : '?';
    return `${base}${sep}${param}=${value}${hash !== undefined ? `#${hash}` : ''}`;
}

const TRIP_RU_HOST = /^ru\.trip\.com$/i;

/**
 * Открывает ru.trip.com в рублях. «Мир» Trip.com принимает только при валюте RUB
 * (их страница об оплате, проверено 29.09.2026), а сайт открывал его в EUR или USD —
 * валюту брали по стране поездки, а не по читателю. На редиректе, а не в билдерах:
 * так разом покрыты витрины /stay, карточки и ссылки из текстов постов.
 * 30% RU-читателей — в России и Беларуси (docs/audits/AFFILIATE_NETWORKS_2026-09-29.md §5).
 * Строкой, как и withNetworkSubId: остальные параметры не перекодируются.
 */
export function withReaderCurrency(target) {
    if (typeof target !== 'string') return target;
    let parsed;
    try {
        parsed = new URL(target);
    } catch {
        return target;
    }
    if (!TRIP_RU_HOST.test(parsed.hostname) || parsed.searchParams.get('curr') === 'RUB') return target;
    const [base, hash] = target.split('#');
    const [path, query = ''] = base.split('?');
    let found = false;
    const params = query.split('&').filter((p) => p && !/^barCurr=/i.test(p)).map((p) => {
        if (!/^curr=/i.test(p)) return p;
        found = true;
        return 'curr=RUB';
    });
    if (!found) params.push('curr=RUB');
    return `${path}?${params.join('&')}${hash !== undefined ? `#${hash}` : ''}`;
}

export function isAllowedDestination(host, extraDomains = new Set()) {
    const h = (host || '').toLowerCase().replace(/\.$/, '');
    for (const domain of [...DESTINATION_DOMAINS, ...extraDomains]) {
        if (h === domain || h.endsWith(`.${domain}`)) return true;
    }
    return false;
}
